/**
 * ============================================================
 * KEJJA OS — src/pages/admin/AdminEmailsPage.tsx
 * Email delivery log + stats.
 * ============================================================
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Mail, Search, CheckCircle2, XCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const API = '/api/v1';

interface EmailLog {
  id: string;
  userId: string | null;
  userEmail: string | null;
  userName: string | null;
  toEmail: string;
  subject: string;
  template: string;
  status: string;
  errorMessage: string | null;
  sentAt: string | null;
  createdAt: string;
}

interface EmailStats {
  total7d: number;
  sent7d: number;
  failed7d: number;
  bounced7d: number;
  byTemplate: Array<{ template: string; count: number; sent: number; failed: number }>;
  recent: EmailLog[];
}

export default function AdminEmailsPage() {
  const [status, setStatus] = useState<string>('');
  const [template, setTemplate] = useState<string>('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const LIMIT = 50;

  const { data: stats } = useQuery<EmailStats>({
    queryKey: ['admin', 'emails', 'stats'],
    queryFn: async () => {
      const r = await fetch(`${API}/admin/emails/stats`, { credentials: 'include' });
      return r.json();
    },
    refetchInterval: 30_000,
  });

  const { data, isLoading } = useQuery<{ logs: EmailLog[]; total: number }>({
    queryKey: ['admin', 'emails', 'logs', page, status, template, search],
    queryFn: async () => {
      const q = new URLSearchParams();
      q.set('limit', String(LIMIT));
      q.set('offset', String(page * LIMIT));
      if (status)   q.set('status', status);
      if (template) q.set('template', template);
      if (search)   q.set('search', search);
      const r = await fetch(`${API}/admin/emails/logs?${q}`, { credentials: 'include' });
      return r.json();
    },
  });

  const logs = data?.logs ?? [];
  const total = data?.total ?? 0;
  const maxPage = Math.max(0, Math.ceil(total / LIMIT) - 1);

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold">Email delivery</h1>
        <p className="text-xs text-slate-400 mt-0.5">Last 7 days · ACS sends logged.</p>
      </div>

      {/* Stats row */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Stat label="Total (7d)"  value={stats.total7d}   color="sky" />
          <Stat label="Sent"        value={stats.sent7d}    color="emerald" />
          <Stat label="Failed"      value={stats.failed7d}  color="rose" />
          <Stat label="Bounced"     value={stats.bounced7d} color="amber" />
        </div>
      )}

      {/* By template */}
      {stats && stats.byTemplate.length > 0 && (
        <div className="bg-slate-900 border border-slate-800 rounded-lg p-4">
          <div className="text-xs font-semibold text-slate-300 mb-2">By template (30d)</div>
          <div className="space-y-2">
            {stats.byTemplate.map((t) => {
              const sentPct = t.count ? Math.round((t.sent / t.count) * 100) : 0;
              return (
                <div key={t.template}>
                  <div className="flex items-baseline justify-between text-xs mb-1">
                    <div className="font-mono text-slate-300 capitalize">{t.template}</div>
                    <div className="text-slate-400 tabular-nums">{t.sent}/{t.count} · {sentPct}%</div>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden">
                    <div className="h-full bg-emerald-500" style={{ width: `${sentPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            placeholder="email or subject…"
            className="bg-slate-900 border border-slate-800 rounded-md pl-8 pr-3 h-9 text-sm text-slate-100 placeholder-slate-500 w-56 focus:outline-none focus:border-slate-600"
          />
        </div>
        <select value={status} onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          className="bg-slate-900 border border-slate-800 rounded-md h-9 px-2 text-xs text-slate-100">
          <option value="">All status</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="bounced">Bounced</option>
        </select>
        <select value={template} onChange={(e) => { setTemplate(e.target.value); setPage(0); }}
          className="bg-slate-900 border border-slate-800 rounded-md h-9 px-2 text-xs text-slate-100">
          <option value="">All templates</option>
          <option value="welcome">Welcome</option>
          <option value="otp">OTP</option>
          <option value="notification">Notification</option>
          <option value="test">Test</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : logs.length === 0 ? (
        <div className="text-center py-10 text-slate-500 text-sm">
          <Mail className="w-8 h-8 mx-auto mb-2 opacity-30" />
          No emails logged yet.
        </div>
      ) : (
        <div className="bg-slate-900 border border-slate-800 rounded-lg overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-slate-400 border-b border-slate-800">
                <th className="p-3 font-medium w-8"></th>
                <th className="p-3 font-medium">To</th>
                <th className="p-3 font-medium">Subject</th>
                <th className="p-3 font-medium">Template</th>
                <th className="p-3 font-medium">When</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {logs.map((l) => (
                <tr key={l.id}>
                  <td className="p-3">
                    <StatusIcon status={l.status} />
                  </td>
                  <td className="p-3">
                    <div className="text-slate-200 truncate max-w-xs">{l.toEmail}</div>
                    {l.userName && <div className="text-[10px] text-slate-500 truncate">{l.userName}</div>}
                  </td>
                  <td className="p-3">
                    <div className="text-slate-100 truncate max-w-md">{l.subject}</div>
                    {l.errorMessage && <div className="text-[10px] text-rose-400 truncate max-w-md mt-0.5">{l.errorMessage}</div>}
                  </td>
                  <td className="p-3">
                    <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 capitalize">{l.template}</span>
                  </td>
                  <td className="p-3 text-slate-400 whitespace-nowrap">{formatRel(l.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div className="flex items-center justify-between text-xs text-slate-400">
        <div>{total.toLocaleString()} total · page {page + 1} of {maxPage + 1}</div>
        <div className="flex gap-1">
          <button disabled={page === 0} onClick={() => setPage(page - 1)}
            className="px-3 py-1 rounded hover:bg-slate-800 disabled:opacity-30">Prev</button>
          <button disabled={page >= maxPage} onClick={() => setPage(page + 1)}
            className="px-3 py-1 rounded hover:bg-slate-800 disabled:opacity-30">Next</button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, color }: { label: string; value: number; color: 'sky' | 'emerald' | 'rose' | 'amber' }) {
  const cls = {
    sky: 'text-sky-400',
    emerald: 'text-emerald-400',
    rose: 'text-rose-400',
    amber: 'text-amber-400',
  }[color];
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-lg p-3">
      <div className={cn('text-2xl font-bold tabular-nums', cls)}>{value.toLocaleString()}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-0.5">{label}</div>
    </div>
  );
}

function StatusIcon({ status }: { status: string }) {
  if (status === 'sent') return <CheckCircle2 className="w-4 h-4 text-emerald-400" aria-label="sent" />;
  if (status === 'failed') return <XCircle className="w-4 h-4 text-rose-400" aria-label="failed" />;
  if (status === 'bounced') return <AlertTriangle className="w-4 h-4 text-amber-400" aria-label="bounced" />;
  return <Loader2 className="w-4 h-4 text-slate-400 animate-spin" aria-label="queued" />;
}

function formatRel(iso: string): string {
  const d = Date.now() - new Date(iso).getTime();
  const m = Math.round(d / 60000);
  if (m < 1) return 'now';
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  return `${days}d ago`;
}
